import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth, verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { CheckCircle2, Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react';

/**
 * Tela branded de redefinição de senha (handler customizado do Firebase).
 *
 * O e-mail de redefinição aponta para /redefinir-senha?oobCode=XXX (em vez da
 * página genérica do Firebase). Aqui validamos o código com
 * verifyPasswordResetCode, mostramos o formulário de nova senha e aplicamos
 * com confirmPasswordReset — tudo com a identidade visual da IAT.
 */
const PasswordReset = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('verifying'); // verifying | form | success | error
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const oobRef = useRef(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const params = new URLSearchParams(location.search);

    // Modo preview (?preview=form|success|error|verifying) — só pra ver o layout.
    const preview = params.get('preview');
    if (preview) {
      if (preview === 'form') setEmail('voce@exemplo.com.br');
      setStatus(preview);
      return;
    }

    const oobCode = params.get('oobCode') || params.get('code');
    if (!oobCode) {
      setStatus('error');
      return;
    }
    oobRef.current = oobCode;

    const auth = getAuth();
    verifyPasswordResetCode(auth, oobCode)
      .then((mail) => {
        setEmail(mail || '');
        setStatus('form');
      })
      .catch((err) => {
        console.error('[PasswordReset] verifyPasswordResetCode falhou:', err);
        setStatus('error');
      });
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (password.length < 6) {
      setFormError('A senha precisa ter ao menos 6 caracteres.');
      return;
    }
    if (password !== confirm) {
      setFormError('As senhas não conferem.');
      return;
    }
    setSubmitting(true);
    try {
      const auth = getAuth();
      await confirmPasswordReset(auth, oobRef.current, password);
      setStatus('success');
    } catch (err) {
      console.error('[PasswordReset] confirmPasswordReset falhou:', err);
      setFormError('Não foi possível redefinir. O link pode ter expirado — solicite um novo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow p-8 text-center">
        {status === 'verifying' && (
          <>
            <Loader2 className="mx-auto mb-4 animate-spin text-[#FFC107]" size={48} />
            <h1 className="text-xl font-semibold mb-2 text-[#0E0E12]">Validando seu link…</h1>
            <p className="text-gray-600">Só um instante.</p>
          </>
        )}

        {status === 'form' && (
          <>
            <h1 className="text-xl font-semibold mb-2 text-[#0E0E12]">Criar nova senha</h1>
            {email && (
              <p className="text-gray-600 mb-6">
                Defina a nova senha da conta <span className="font-medium text-[#0E0E12]">{email}</span>.
              </p>
            )}
            <form onSubmit={handleSubmit} className="text-left">
              <label className="block text-sm font-medium text-[#0E0E12] mb-1">Nova senha</label>
              <div className="relative mb-4">
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-[#FFC107]"
                  placeholder="Mínimo 6 caracteres"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label={showPwd ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <label className="block text-sm font-medium text-[#0E0E12] mb-1">Confirmar nova senha</label>
              <input
                type={showPwd ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-[#FFC107]"
                placeholder="Repita a senha"
              />
              {formError && <p className="text-red-500 text-sm mb-4">{formError}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#FFC107] hover:bg-[#1A1A20] text-white py-2 px-6 rounded-lg font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {submitting && <Loader2 className="animate-spin" size={18} />}
                {submitting ? 'Salvando…' : 'Redefinir senha'}
              </button>
            </form>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle2 className="mx-auto mb-4 text-green-500" size={48} />
            <h1 className="text-xl font-semibold mb-2 text-[#0E0E12]">Senha redefinida!</h1>
            <p className="text-gray-600 mb-6">Já pode entrar na plataforma com a nova senha.</p>
            <button
              onClick={() => navigate('/login')}
              className="bg-[#FFC107] hover:bg-[#1A1A20] text-white py-2 px-6 rounded-lg font-semibold transition-colors"
            >
              Ir para o login
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <AlertCircle className="mx-auto mb-4 text-red-500" size={48} />
            <h1 className="text-xl font-semibold mb-2 text-[#0E0E12]">Link inválido ou expirado</h1>
            <p className="text-gray-600 mb-6">
              Este link de redefinição não é mais válido. Volte ao login e solicite um novo e-mail.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="bg-[#FFC107] hover:bg-[#1A1A20] text-white py-2 px-6 rounded-lg font-semibold transition-colors"
            >
              Ir para o login
            </button>
          </>
        )}

        <p className="mt-6 text-sm text-gray-500">
          Dúvidas? <a href="mailto:contato@iatconsulting.com.br" className="text-[#0E0E12] hover:underline">contato@iatconsulting.com.br</a>
        </p>
      </div>
    </div>
  );
};

export default PasswordReset;
